import React, { useEffect, useState, useCallback } from 'react';
import { Ticket, Check, Copy, Loader2, RefreshCw, ShoppingCart, Euro, ChevronDown, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { PRICES } from '../config/prices';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

// Custom-amount vouchers must be at least €5 — smaller values waste the
// payment-processor floor and the price tiers we sell already cover lower
// amounts as presets.
const MIN_CUSTOM_AMOUNT_CENTS = 500;

const formatVoucherAmount = (v) => {
  if (v.discountType === 'amount') {
    return `€${((v.discountAmountCents || 0) / 100).toFixed(2)} off`;
  }
  const pct = (v.discountPercent || 0) * 100;
  return `${pct.toFixed(0)}% off`;
};

const STATUS_STYLES = {
  active: 'bg-gray-200 text-gray-900',
  used: 'bg-gray-100 text-gray-500',
  expired: 'bg-gray-100 text-gray-500',
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString();
  } catch (_e) {
    return null;
  }
};

const sourceLabel = (v) => {
  if (v.source === 'remainder') return 'Leftover from a previous purchase';
  if (v.source === 'purchase') {
    const d = formatDate(v.createdAt);
    return d ? `Bought ${d}` : 'Bought';
  }
  return 'Voucher';
};

// Preset cards describe the unlock in plain language — easier to grok than
// "M" / "L" since vouchers can be gifted to people who don't know our sizing.
const PRESET_AMOUNTS_CENTS = [
  { label: 'For a Size M image', sub: '~40x30 cm', cents: Math.round((PRICES.M || 2.49) * 100) },
  { label: 'For a Size L image', sub: '~80x60 cm', cents: Math.round((PRICES.L || 4.49) * 100) },
];

const VouchersDialog = ({ open, onOpenChange }) => {
  const [vouchers, setVouchers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  // Explicit mode toggle so the custom card visually selects the moment it
  // is clicked, without requiring the user to type a number first.
  const [mode, setMode] = useState('preset'); // 'preset' | 'custom'
  const [selectedAmountCents, setSelectedAmountCents] = useState(PRESET_AMOUNTS_CENTS[0].cents);
  const [customAmountInput, setCustomAmountInput] = useState('');
  const [buyLoading, setBuyLoading] = useState(false);
  const [buyError, setBuyError] = useState(null);
  const [inactiveOpen, setInactiveOpen] = useState(false);

  const fetchVouchers = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/user/vouchers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Failed to load vouchers');
      const data = await r.json();
      setVouchers(data.vouchers || []);
    } catch (e) {
      setError(e.message || 'Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchVouchers();
  }, [open, fetchVouchers]);

  const handleBuy = async () => {
    setBuyError(null);
    let amount = selectedAmountCents;
    const isCustom = mode === 'custom';
    if (isCustom) {
      const parsed = Math.round(parseFloat(customAmountInput) * 100);
      amount = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    }
    if (!amount || amount <= 0) {
      setBuyError('Pick a preset or enter a custom amount.');
      return;
    }
    if (isCustom && amount < MIN_CUSTOM_AMOUNT_CENTS) {
      setBuyError('Custom amounts start at €5.00.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setBuyError('Please sign in first.');
      return;
    }
    setBuyLoading(true);
    try {
      const r = await fetch(`${API_URL}/buy-voucher`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount_cents: amount }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to start checkout');
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      throw new Error('Checkout URL missing');
    } catch (e) {
      setBuyError(e.message || 'Failed to start checkout');
      setBuyLoading(false);
    }
  };

  const handleCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode((c) => (c === code ? null : c)), 1500);
    } catch (_e) {
      // Clipboard API may be blocked in non-secure contexts; the code is
      // still selectable in the dialog.
    }
  };

  const renderVoucherRow = (v) => (
    <div
      key={v.id || v.code}
      className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2"
    >
      <div className="flex items-start justify-between gap-2">
        <code className="font-mono text-sm font-semibold break-all">{v.code}</code>
        <span
          className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
            STATUS_STYLES[v.status] || STATUS_STYLES.expired
          }`}
        >
          {v.status === 'active' ? 'Valid' : v.status === 'used' ? 'Already used' : 'Expired'}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 text-xs text-gray-700">
        <div>
          <p className="font-medium text-gray-900">{formatVoucherAmount(v)}</p>
          <p className="text-gray-500">
            {sourceLabel(v)}
            {v.expiryDate && (
              <> · expires {new Date(v.expiryDate).toLocaleDateString()}</>
            )}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleCopy(v.code)}
          disabled={v.status !== 'active'}
          className={`flex-shrink-0 transition-colors ${
            copiedCode === v.code ? 'border-gray-900 text-gray-900' : ''
          }`}
        >
          {copiedCode === v.code ? (
            <>
              <Check className="h-4 w-4 mr-1" /> Copied
            </>
          ) : (
            <>
              <Copy className="h-4 w-4 mr-1" /> Copy
            </>
          )}
        </Button>
      </div>
    </div>
  );

  const activeVouchers = (vouchers || []).filter((v) => v.status === 'active');
  const inactiveVouchers = (vouchers || []).filter((v) => v.status !== 'active');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:rounded-2xl p-0 overflow-hidden max-w-lg">
        <div className="bg-gradient-to-br from-gray-900 to-gray-700 text-white px-6 py-5 flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center">
            <Ticket className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <DialogTitle className="text-lg font-semibold">Your vouchers</DialogTitle>
            <p className="text-xs text-gray-300 mt-0.5">
              Bought vouchers and leftovers from previous purchases. Codes can be shared.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchVouchers}
            className="text-white/80 hover:text-white p-1 rounded-full"
            title="Refresh"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto space-y-5">
          {/* Buy a voucher */}
          <section className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <ShoppingCart className="h-3.5 w-3.5" /> Buy a voucher
            </div>
            <div className="space-y-2">
              {PRESET_AMOUNTS_CENTS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => { setMode('preset'); setSelectedAmountCents(p.cents); }}
                  className={`w-full p-3 rounded-lg border text-sm transition-all flex items-center justify-between gap-2 ${
                    mode === 'preset' && selectedAmountCents === p.cents
                      ? 'border-gray-900 bg-gray-100 font-semibold text-gray-900 shadow-sm'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="text-left">
                    <div>{p.label}</div>
                    <div className="text-[11px] text-gray-500">{p.sub}</div>
                  </div>
                  <div className="font-semibold">€{(p.cents / 100).toFixed(2)}</div>
                </button>
              ))}
              <div
                role="button"
                tabIndex={0}
                onClick={() => setMode('custom')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMode('custom'); } }}
                className={`block w-full p-3 rounded-lg border text-sm transition-all cursor-pointer ${
                  mode === 'custom' ? 'border-gray-900 bg-gray-100 shadow-sm' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div>Custom amount</div>
                    <div className="text-[11px] text-gray-500">Minimum €5</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Euro className="h-3.5 w-3.5 text-gray-400" />
                    <Input
                      type="number"
                      min="5"
                      step="0.01"
                      placeholder="5.00"
                      value={customAmountInput}
                      onChange={(e) => setCustomAmountInput(e.target.value)}
                      onFocus={() => setMode('custom')}
                      onClick={(e) => e.stopPropagation()}
                      className="w-20 border-0 p-0 h-auto bg-transparent text-sm font-semibold text-right focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>
                </div>
              </div>
            </div>
            <Button
              onClick={handleBuy}
              disabled={buyLoading}
              className="w-full bg-gray-900 hover:bg-gray-700 text-white border-0 shadow-md text-sm font-semibold"
            >
              {buyLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</>
              ) : (
                <><ShoppingCart className="h-4 w-4 mr-2" /> Buy voucher</>
              )}
            </Button>
            {buyError && <p className="text-xs text-red-500 text-center">{buyError}</p>}
            <p className="text-[11px] text-gray-500 text-center">
              Codes can be shared — give them to friends or keep for yourself.
            </p>
          </section>

          <div className="border-t border-gray-200" />

          {loading && vouchers === null && (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading vouchers…
            </div>
          )}
          {error && (
            <div className="text-sm text-red-500 text-center py-4">{error}</div>
          )}
          {vouchers && vouchers.length === 0 && !loading && (
            <div className="text-sm text-gray-500 text-center py-8">
              You don't have any vouchers yet. Leftover credit from a fixed-amount voucher
              redemption will show up here.
            </div>
          )}
          {activeVouchers.map(renderVoucherRow)}
          {inactiveVouchers.length > 0 && (
            <div className="pt-1">
              <button
                type="button"
                onClick={() => setInactiveOpen((x) => !x)}
                className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
              >
                {inactiveOpen
                  ? <ChevronDown className="h-3.5 w-3.5" />
                  : <ChevronRight className="h-3.5 w-3.5" />
                }
                Used &amp; expired ({inactiveVouchers.length})
              </button>
              {inactiveOpen && (
                <div className="mt-2 space-y-3">
                  {inactiveVouchers.map(renderVoucherRow)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
          <Button
            onClick={() => onOpenChange(false)}
            className="bg-gray-900 hover:bg-gray-700 text-white border-0"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VouchersDialog;
